<template>
	<div class="income-info-box__wrapper">
		<div class="income-info-box">
			<RaggedBoxBackground slug="income-info-box-bg" />
			<div class="income-info-box__content">
				<div class="income-info-box__basic">
					<div>
						<div class="income-info-box__basic-text">
							<span
								v-show="isIncomeVariant && incomeBoostActive"
								class="income-info-box__boost"
							>
								{{ INCOME_BOOST_COEFFICIENT }}x
							</span>
							{{
								formatNumber(
									isIncomeVariant
										? income.value
										: income.storage,
								)
							}}
						</div>
						<AppMainIcon
							class="income-info-box__basic-icon"
							:iconName="income.type"
							:iconSize="16"
						/>
					</div>
					<div>
						<div class="income-info-box__basic-text">
							{{
								formatNumber(
									isIncomeVariant
										? income.storage
										: income.value,
								)
							}}
						</div>

						<AppMainIcon
							class="income-info-box__basic-icon"
							:iconName="income.type"
							:iconSize="16"
						/>
					</div>
					<div>
						<div class="income-info-box__basic-text">
							{{
								isIncomeVariant &&
								income.maxStorage === undefined
									? "-"
									: formatNumber(
											isIncomeVariant
												? (income.maxStorage as number)
												: income.value + income.storage,
										)
							}}
						</div>
						<AppMainIcon
							v-if="!isIncomeVariant || income.maxStorage"
							class="income-info-box__basic-icon"
							:iconName="income.type"
							:iconSize="16"
						/>
					</div>
				</div>
				<div class="income-info-box__arrow" @click="toggleMore">
					<div :class="`arrow--${showMore ? 'up' : 'down'}-26`" />
				</div>
			</div>
		</div>
		<div v-if="showMore" class="income-info-box__more">
			<div>
				{{
					translate(
						`${resourceKey}.${income.type.replace("fooddiff", "food").replace("populationdiff", "workforce")}_description`,
					)
				}}
			</div>
			<div
				class="income-info-box__source"
				v-for="(source, i) in sources"
				:key="i"
			>
				<div class="income-info-box__source-left">
					{{
						`${canTranslate(source.sourceText) ? translate(source.sourceText) : source.sourceText} - `
					}}
					<span class="income-info-box__source-count">
						{{ `${source.count}x` }}
					</span>
				</div>
				<div class="income-info-box__source-left">
					{{
						`${isProducing(source) ? translate("ui.produces") : translate("ui.consumes")}: ${formatNumber(Math.abs((source as ResourceIncomeSourceItem).production || (source as ResourceConsumptionSourceItem).consumption))}`
					}}

					<AppMainIcon
						class="income-info-box__source-icon"
						:iconName="income.type"
						:iconSize="16"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import { INCOME_BOOST_COEFFICIENT } from "@/config/constants";
import { useEventStore } from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import useRuntimeStore from "@/store/useRuntimeStore";
import type {
	ResourceConsumption,
	ResourceConsumptionSourceItem,
	ResourceIncome,
	ResourceIncomeSourceItem,
} from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { canTranslate, translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import AppMainIcon from "../AppMainIcon.vue";

interface Props {
	income: ResourceIncome | ResourceConsumption;
}

const { income } = defineProps<Props>();

const emit = defineEmits<{
	toggle: [isExpanded: boolean];
}>();

const showMore = ref<boolean>(false);

const toggleMore = (): void => {
	showMore.value = !showMore.value;
	emit("toggle", showMore.value);
};

const isIncomeVariant = computed((): boolean => "incomeSources" in income);
const runtimeStore = useRuntimeStore();
const { incomeBoostActive } = storeToRefs(runtimeStore);

const eventStore = useEventStore();
const event = storeToRefs(eventStore);

const isProducing = (
	value: ResourceIncomeSourceItem | ResourceConsumptionSourceItem,
): boolean => {
	const source =
		(value as ResourceIncomeSourceItem).production ||
		(value as ResourceConsumptionSourceItem).consumption;
	return isIncomeVariant.value ? source >= 0 : source <= 0;
};

const resourceKey = computed((): string => {
	if (income.type === ResourceType.EVENTIUM) {
		return `event.${event.attributes.value.type}_event`;
	}
	return "resource";
});

// Toto je len kvoli typom, lebo v template to nebralo.
const sources = computed(
	():
		| ResourceIncome["incomeSources"]
		| ResourceConsumption["consumptionSources"] => {
		return (
			(income as ResourceIncome).incomeSources ||
			(income as ResourceConsumption).consumptionSources
		);
	},
);
</script>

<style lang="scss" scoped>
.income-info-box {
	position: relative;
	width: 100%;
	@include flex-col-center;
	filter: drop-shadow(0 0 0.125rem rgba(0, 0, 0, 0.4));
	height: 2.5rem;
	&__wrapper {
		width: 100%;
		@include flex-col-center;
		position: relative;
		flex-shrink: 0;
		z-index: 0;
	}
	&__content {
		display: flex;
		position: relative;
		justify-content: center;
		width: 100%;
		height: 100%;
		color: #372615;
	}
	&__basic {
		width: 100%;
		@include flex-row-center;
		& > div {
			width: 33.3333%;
			height: 100%;
			@include flex-row-center;
			color: #372615;
			font-size: 0.906rem;
		}
		&-text {
			margin-right: 0.2rem;
			margin-bottom: 0.2rem;
		}
	}
	&__boost {
		margin-bottom: 0.2rem;
		color: #19b345;
	}
	&__arrow {
		background: rgba($color: #917138, $alpha: 0.6);
		width: 2.2rem;
		height: 100%;
		@include flex-row-center;
		flex-shrink: 0;
		cursor: pointer;
	}
	&__more {
		position: relative;
		z-index: -1;
		margin-top: -0.5rem;
		box-shadow: inset 0 0 0.8rem 0 rgba(29, 30, 33, 0.8);
		background-image: linear-gradient(to top, #ab8849, #f2dcb5);
		padding: 1rem 0.5rem 0.5rem;
		width: 95%;
		color: #372615;
		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 0.6;
			// NOTE: Color-burn blending is better for dark colors
			mix-blend-mode: color-burn;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat center;
			background-size: 40%;
			width: 100%;
			height: 100%;
			content: "";
		}
	}
	&__source {
		display: flex;
		width: 100%;
		@include flex-row-between;
		margin-top: 0.25rem;
		box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
		border-width: 0.063rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #61667f, #787c8b);
		border-image-slice: 1;
		background-image: linear-gradient(to top, #3b3c48, #595d6d);
		padding: 0.125rem 0.5rem;
		color: #372615;
		color: #fff3dd;
		font-size: 0.875rem;
		& > div {
			@include flex-row-center;
			gap: 0.25rem;
			margin-bottom: 0.15rem;
		}
		&-count {
			font-weight: bold;
		}
	}

	:deep(.ragged-box-bg) {
		position: absolute;
		width: 100%;
		height: 100%;
	}
}
</style>
